import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { Subscription, merge, of as observableOf } from 'rxjs';
import { catchError, startWith, switchMap, map, filter } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { RegelDetailsComponent } from '../regel-details/regel-details.component';
import { IPreSalesRow, LeverancierStatus, LeverancierStatusString, RegelsService, RequestViewMode } from '../regels.service';
import { SharedNotificationService } from '../shared-notification.service';
import { AutocompleteService, ISelectItem, IVbnFeatureValue } from '../autocomplete.service';
import {PaginatedResult} from "../fetch-data/PaginatedResult";

@Component({
    selector: 'app-regels-table',
    templateUrl: './regels-table.component.html',
    styleUrls: ['./regels-table.component.css']
})
export class RegelsTableComponent implements OnInit, OnDestroy {
    displayedColumns: string[] = [
        'regelNr',
        'debiteurNaam',
        'thumbnail',
        'artikelNr',
        'productNaam',
        'potmaat',
        'hoogte',
        'afleverperiodeVan',
        'deadlineInkoopDate',
        'aantalGevraagd',
        'inkoperNaam',
        'status',
        'acties',
        'isChanged',
    ];
    data: IPreSalesRow[] = [];
    dataSource: MatTableDataSource<IPreSalesRow> = new MatTableDataSource<IPreSalesRow>();
    totalRecords = 0;
    pageSize = 100; // Default page size
    pageIndex = 0;

    saveButtonIsEnabled: boolean = false;

    @Input() onlyShowArchivedRegels: boolean;
    @Input() version: number;
    regelsFilter: EventEmitter<any> = new EventEmitter();
    @Output() onSaveEvent = new EventEmitter();

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    selectedEan: number;
    leverancierStatusType = LeverancierStatusString;
    leverancierStatusKeys = Object.values(LeverancierStatusString).filter(value => typeof value === 'number');

    supplierAssignedIDs: ISelectItem[];
    vbnFeatureValues: IVbnFeatureValue[];
    onSupplierChangeSubscription: Subscription;

    constructor(
        private _regelsService: RegelsService,
        private _snackBar: MatSnackBar,
        private _dialog: MatDialog,
        private _details: MatDialog,
        private _notificationService: SharedNotificationService,
        private _autocompleteService: AutocompleteService
    ) {}

    ngOnInit() {
        this.dataSource.sort = this.sort;

        // Set initial selected EAN
        if (this.selectedEan == undefined) {
            this.selectedEan = this._notificationService.getOnSupplierChange || null;
        }

        this._autocompleteService
            .getSupplierAssignedIDs('', this.selectedEan)
            .subscribe(val => (this.supplierAssignedIDs = val.data));
        this._autocompleteService
            .getVbnFeatureValues('S02')
            .subscribe(val => (this.vbnFeatureValues = val));

        this.loadPagedData();

        // Subscription to external changes for updating the table
        this.onSupplierChangeSubscription = this._notificationService.onSupplierChangeObservable.subscribe({
            next: (ean: number) => {
                this.selectedEan = ean;
                this.loadPagedData();
            },
            error: (err: Error) =>
                console.error('Error setting selectedEan: ', err)
        });
    }

    loadPagedData() {
        this._notificationService.setIsLoadingRowResults(true);
        this._regelsService.getSupplierRequest(this.selectedEan, this.pageIndex, this.pageSize, this.onlyShowArchivedRegels ? RequestViewMode.Archived : RequestViewMode.Open).pipe(
            map((result: PaginatedResult<IPreSalesRow>) => {
                this._notificationService.setIsLoadingRowResults(false);
                return result;
            }),
            catchError(() => {
                this._notificationService.setIsLoadingRowResults(false);
                this.totalRecords = 0;
                return observableOf({ items: [], totalCount: 0 });
            })
        ).subscribe(result => {
            this.data = result.items;
            this.totalRecords = result.totalCount;

            this.data.filter(row => row.leverancierDetails).forEach(row => {
                const vbnFeatureValue = this.vbnFeatureValues.find(vbn =>
                    vbn.featureValue.replace(/^0+/, '') == row.leverancierDetails.hoogte?.toString()
                );
                if (vbnFeatureValue) row.vbnHoogte = vbnFeatureValue.description;
            });

            this.dataSource.data = this.data;

            this.dataSource.sort = this.sort;
            this.enableDisableSaveButton();
        });
    }

    onPageChange(event: any) {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.loadPagedData();
    }

    enableDisableSaveButton() {
        this.saveButtonIsEnabled = this.data.some(d => d.isChanged);
    }

    hasChanges() {
        return this.data.some(r => r.isChanged);
    }

    getReadyForSubmissionCount() {
        return this.data.filter(d => d.status !== LeverancierStatus.Nieuw).length;
    }

    onChange(item: IPreSalesRow) {
        item.isChanged = true;

        this._regelsService.patchStatus(item).subscribe(
            () => {},
            err => {
                console.error('Error while patching status: ', err);
                this._snackBar.open(
                    'Er is iets fout gegaan tijdens het opslaan. Probeer opnieuw.',
                    null,
                    {
                        duration: 2000,
                        panelClass: ['kp-status-message', 'kp-status-message-error'],
                    }
                );
            }
        );

        this.enableDisableSaveButton();
    }

    onSave() {
        const changedEntities = this.data.filter(d => d.isChanged);
        if (changedEntities.length === 0) {
            return;
        }

        this._notificationService.setIsLoadingRowResults(true);

        const message = changedEntities.length > 1
            ? `Weet u zeker dat u de ${changedEntities.length} gewijzigde regels wilt overdragen aan Hamiplant?`
            : 'Weet u zeker dat u de gewijzigde regel wilt overdragen aan Hamiplant?';

        const confirmDialog = this._dialog.open(ConfirmDialogComponent, {
            data: { title: 'Bevestigen', message },
        });

        confirmDialog.afterClosed().subscribe(result => {
            if (result === true) {
                changedEntities.forEach((item, index) => {
                    item.confirmationDate = new Date(Date.now());
                    if (item.status === LeverancierStatus.Nieuw) {
                        item.status = LeverancierStatus.Voltooid;
                    }
                    item.isChanged = false;

                    this._regelsService.update(item).subscribe(() => {
                        if (index + 1 === changedEntities.length) {
                            const ids = changedEntities.map(a => a.id);
                            this._regelsService.getOfferteExport(ids).subscribe();

                            this._snackBar.open('Succesvol opgeslagen.', null, {
                                duration: 2000,
                                panelClass: ['kp-status-message', 'kp-status-message-success'],
                            });
                            this._notificationService.setIsLoadingRowResults(false);
                            this.onSaveEvent.emit();
                            this.loadPagedData();
                        }
                    }, err => {
                        console.error('Error updating item: ', err);
                        this._snackBar.open(
                            'Er is iets fout gegaan tijdens het opslaan. Probeer opnieuw.',
                            null,
                            {
                                duration: 2000,
                                panelClass: ['kp-status-message', 'kp-status-message-error'],
                            }
                        );
                    });
                });
            } else {
                this._notificationService.setIsLoadingRowResults(false);
            }
        });
    }

    onDetailsOpen(id: string) {
        this._details.open(RegelDetailsComponent, {
            data: { id },
            // Make dialog large as requested
            width: '95vw',
            maxWidth: '95vw',
            height: '90vh',
            // Optional: keep within viewport if content grows
            maxHeight: '90vh',
            panelClass: 'regel-details-dialog'
        }).afterClosed().subscribe(obj => {
            if (obj?.refreshAllRows) {
                this.onSaveEvent.emit(true);
                this.loadPagedData();
            } else if (obj?.result) {
                const item = this.data.find(d => d.id === id);
                if (item) {
                    item.status = obj.status;
                    this.onChange(item);
                }
            }
        });
    }

    ngOnDestroy() {
        if (this.onSupplierChangeSubscription) {
            this.onSupplierChangeSubscription.unsubscribe();
        }
    }

    trackById(index, item) {
        return item.id;
    }
}